import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ShieldIcon,
  LockIcon,
  ServerIcon,
  UsersIcon,
  AlertCircleIcon,
  MailIcon,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { config } from '../lib/config';

const pageUrl = `${config.appUrl}/security`;
const title = 'Security at CoRATES - How We Protect Research Data';
const description =
  'How CoRATES protects your research data: encryption in transit and at rest, authentication and access controls, abuse prevention, and infrastructure hardening.';

export const Route = createFileRoute('/security')({
  headers: () => ({
    'Cache-Control': 'public, s-maxage=3600, stale-while-revalidate=86400',
  }),
  head: () => ({
    meta: [
      { title },
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: pageUrl },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ],
    links: [{ rel: 'canonical', href: pageUrl }],
  }),
  component: SecurityPage,
});

function SecurityPage() {
  return (
    <div className='flex min-h-screen flex-col'>
      <Navbar />

      <main className='flex-1 py-12'>
        <div className='mx-auto max-w-3xl px-6'>
          <h1 className='mb-2 text-4xl font-bold text-gray-900'>Security</h1>
          <p className='mb-8 text-gray-500'>How we protect your research data</p>

          <div className='flex flex-col gap-8 leading-relaxed text-gray-700'>
            <p>
              At CoRATES, we understand that your research data is valuable and sensitive. We are
              committed to implementing robust security measures to protect your information and
              ensure the integrity of your evidence synthesis work.
            </p>

            {/* Security Features */}
            <div className='grid gap-6'>
              <div className='rounded-lg bg-gray-50 p-6'>
                <div className='flex items-start gap-4'>
                  <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-100'>
                    <LockIcon className='size-5 text-blue-600' />
                  </div>
                  <div>
                    <h2 className='mb-2 text-lg font-semibold text-gray-900'>Encryption</h2>
                    <p className='text-gray-600'>
                      All data transmitted between your browser and our servers is encrypted using
                      TLS (Transport Layer Security). Data at rest, including uploaded PDFs and
                      application data, is encrypted using industry-standard encryption
                      (AES-256-GCM) provided by our infrastructure providers.
                    </p>
                  </div>
                </div>
              </div>

              <div className='rounded-lg bg-gray-50 p-6'>
                <div className='flex items-start gap-4'>
                  <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-100'>
                    <ShieldIcon className='size-5 text-blue-600' />
                  </div>
                  <div>
                    <h2 className='mb-2 text-lg font-semibold text-gray-900'>Authentication</h2>
                    <p className='text-gray-600'>
                      We support multiple secure authentication methods including OAuth (Google,
                      ORCID) and passwordless login via email, with active session management so you
                      can sign out other devices.
                    </p>
                  </div>
                </div>
              </div>

              <div className='rounded-lg bg-gray-50 p-6'>
                <div className='flex items-start gap-4'>
                  <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-100'>
                    <AlertCircleIcon className='size-5 text-blue-600' />
                  </div>
                  <div>
                    <h2 className='mb-2 text-lg font-semibold text-gray-900'>Abuse Prevention</h2>
                    <p className='text-gray-600'>
                      Cloudflare&apos;s managed WAF and DDoS protection sit in front of every
                      request. Sign-in, sign-up, emailed code, and password reset requests are rate
                      limited per client, as are public forms such as contact and feedback.
                    </p>
                  </div>
                </div>
              </div>

              <div className='rounded-lg bg-gray-50 p-6'>
                <div className='flex items-start gap-4'>
                  <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-100'>
                    <ServerIcon className='size-5 text-blue-600' />
                  </div>
                  <div>
                    <h2 className='mb-2 text-lg font-semibold text-gray-900'>Infrastructure</h2>
                    <p className='text-gray-600'>
                      CoRATES is hosted on Cloudflare's global network, providing enterprise-grade
                      security, DDoS protection, and a Web Application Firewall (WAF) to protect
                      against common web vulnerabilities.
                    </p>
                  </div>
                </div>
              </div>

              <div className='rounded-lg bg-gray-50 p-6'>
                <div className='flex items-start gap-4'>
                  <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-100'>
                    <UsersIcon className='size-5 text-blue-600' />
                  </div>
                  <div>
                    <h2 className='mb-2 text-lg font-semibold text-gray-900'>Access Control</h2>
                    <p className='text-gray-600'>
                      Project-level access controls ensure that only authorized team members can
                      view and edit your research data. You control who has access to your projects
                      and can revoke access at any time. You retain full ownership of your research
                      data. CoRATES does not sell or share your data with third parties.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Responsible Disclosure */}
            <div className='border-t border-gray-200 pt-8'>
              <div className='flex items-start gap-4'>
                <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-100'>
                  <AlertCircleIcon className='size-5 text-amber-600' />
                </div>
                <div>
                  <h2 className='mb-3 text-xl font-semibold text-gray-900'>
                    Responsible Disclosure
                  </h2>
                  <p className='mb-4 text-gray-600'>
                    We take security vulnerabilities seriously. If you believe you have found a
                    security vulnerability in CoRATES, we encourage you to report it to us
                    responsibly. We will not pursue legal action against researchers who report
                    vulnerabilities responsibly and in good faith. Our full{' '}
                    <Link
                      to='/security/disclosure'
                      className='font-medium text-blue-600 hover:text-blue-700'
                    >
                      vulnerability disclosure policy
                    </Link>{' '}
                    covers scope, response times, and safe harbor, and is referenced from{' '}
                    <a
                      href='/.well-known/security.txt'
                      className='font-medium text-blue-600 hover:text-blue-700'
                    >
                      /.well-known/security.txt
                    </a>
                    .
                  </p>
                  <p className='mb-4 text-gray-600'>
                    Please send details of the vulnerability to{' '}
                    <a
                      href='mailto:support@corates.org'
                      className='font-medium text-blue-600 hover:text-blue-700'
                    >
                      support@corates.org
                    </a>
                    . Include as much information as possible, such as:
                  </p>
                  <ul className='mb-4 ml-6 flex list-outside list-disc flex-col gap-1 text-gray-600'>
                    <li>A description of the vulnerability</li>
                    <li>Steps to reproduce the issue</li>
                    <li>Potential impact of the vulnerability</li>
                    <li>Any suggestions for remediation</li>
                  </ul>
                  <p className='text-gray-600'>
                    We will acknowledge receipt of your report within 48 hours and will work with
                    you to understand and address the issue promptly. We ask that you give us
                    reasonable time to investigate and mitigate the vulnerability before making any
                    information public.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className='border-t border-gray-200 pt-8'>
              <div className='flex items-start gap-4'>
                <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-100'>
                  <MailIcon className='size-5 text-blue-600' />
                </div>
                <div>
                  <h2 className='mb-3 text-xl font-semibold text-gray-900'>Security Questions</h2>
                  <p className='text-gray-600'>
                    If you have any questions about our security practices or would like more
                    information, please contact us at{' '}
                    <a
                      href='mailto:support@corates.org'
                      className='font-medium text-blue-600 hover:text-blue-700'
                    >
                      support@corates.org
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
